import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import ru from './locales/ru.json';

/** localStorage key the user's explicit language choice is persisted under. */
export const LANG_STORAGE_KEY = 'vc.lang';

export const LANGUAGES = ['en', 'ru'] as const;
/** Every UI language the SPA ships with; en is the source of truth catalog. */
export type AppLanguage = (typeof LANGUAGES)[number];

export const defaultNS = 'translation';

const resources = {
  en: { translation: en },
  ru: { translation: ru },
} as const;

/** Reads the persisted language choice; null when storage is unavailable or the value is unknown. */
function storedLanguage(): AppLanguage | null {
  try {
    const raw = window.localStorage.getItem(LANG_STORAGE_KEY);
    return raw === 'en' || raw === 'ru' ? raw : null;
  } catch {
    return null;
  }
}

/**
 * First-visit detection: a navigator language starting with `ru` selects ru, everything else
 * falls back to en (the source-of-truth catalog).
 */
function navigatorLanguage(): AppLanguage {
  const language = typeof navigator === 'undefined' ? undefined : navigator.language;
  return language !== undefined && language.toLowerCase().startsWith('ru') ? 'ru' : 'en';
}

/** Detection order: persisted choice first, then the browser language. */
export function detectLanguage(): AppLanguage {
  return storedLanguage() ?? navigatorLanguage();
}

/** Keeps the document language attribute in sync with the UI language. */
function applyDocumentLanguage(language: AppLanguage): void {
  document.documentElement.lang = language;
}

void i18next.use(initReactI18next).init({
  // Resources are bundled statically: init completes synchronously, so no component ever
  // awaits a translation and tests render deterministically without async setup.
  lng: detectLanguage(),
  fallbackLng: 'en',
  supportedLngs: [...LANGUAGES],
  defaultNS,
  resources,
  returnNull: false,
  interpolation: {
    // React already escapes rendered values; double-escaping would mangle names.
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

applyDocumentLanguage(i18next.language === 'ru' ? 'ru' : 'en');

export const i18n: I18nInstance = i18next;

/**
 * Switches the UI language, persists the explicit choice (it overrides detection on the next
 * visit) and updates <html lang>. Storage failures (private mode) keep the choice page-local.
 */
export async function changeLanguage(language: AppLanguage): Promise<void> {
  await i18next.changeLanguage(language);
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, language);
  } catch {
    // Storage unavailable: the choice lives for this page load only.
  }
  applyDocumentLanguage(language);
}
